import {
  Box,
  Button,
  Divider,
  Grid,
  makeStyles,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  cancelTransaction,
  renewTransaction,
  resettleTransaction,
  resyncTransaction,
} from 'api/cms/transaction';
import ConfirmModal from 'components/ConfirmModal';
import _ from 'lodash';
import queryString from 'query-string';
import { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUser } from 'store/user';
import useSWR from 'swr';
import { getUserPlayerById } from '../../../api/cms/player';
import { getUserTransactionList } from '../../../api/cms/user';
import Breadcrumbs from '../../../components/Breadcrumbs';
import DropdownActiveProvider from '../../../components/DropdownActiveProvider';
import ApplicationLayout from '../../../components/layouts/ApplicationLayout';
import TablePaginationActions from '../../../components/table/TablePaginationActions';
import { useDateRange } from '../../../utils/datetime';
import {
  formatInTZ,
  fromDateTimeToOffsetISO,
  toDateAndTimeInTZ,
} from '../../../utils/datetimeHelpers';
import { AllTimeZone, parseTzKeyToOffset } from '../../../utils/timezone';

const useStyles = makeStyles((theme) => ({
  providerDropdown: {
    marginLeft: '10px',
  },
  compactField: {
    minWidth: 140,
    maxWidth: 220,
    width: '100%',
  },
  compactRow: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  searchField: {
    minWidth: 270,
    maxWidth: 480,
    width: '100%',
  },
  dateField: { minWidth: 150, maxWidth: 200, width: '100%' },
  timeField: { minWidth: 120, maxWidth: 160, width: '100%' },
  tzField: { minWidth: 180, maxWidth: 240, width: '100%' },
  topInfoRow: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  filterBar: {
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0, 2),
  },
  buttonRow: {
    marginTop: theme.spacing(2),
  },
  grow: {
    width: '100%',
  },
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
  },
  table: {
    minWidth: 1200,
    tableLayout: 'auto',
    '& th, & td': {
      whiteSpace: 'nowrap',
    },
  },
}));

const UserTransaction = () => {
  let location = useLocation();
  const { search } = useLocation();
  const history = useHistory();
  const classes = useStyles();
  const paramsString = queryString.parse(search);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [page, setPage] = useState(0);
  const [currentProvider, setCurrentProvider] = useState({
    providerCode: '',
    providerId: '',
    providerName: '',
  });

  const defaultSearchObj = {
    ticketNumber: '',
    transactionId: '', // UI still says "Transaction ID" but searches provider_transaction_id
    roundId: '',
    userToken: '',
    logTime: '',
  };
  const [searchObj, setSearch] = useState(defaultSearchObj);
  const [dateRange, setDateRange] = useDateRange(paramsString);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isActionCancel, setIsActionCancel] = useState(false);
  const [isActionRenew, setIsActionRenew] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [{ user }] = useUser();
  const [rangeActive, setRangeActive] = useState(false);

  useEffect(() => {
    const q = queryString.parse(history.location.search);
    const removed = [
      'from_time',
      'to_time',
      'startDate',
      'endDate',
      'startDateValue',
      'endDateValue',
    ].some((k) => k in q);

    if (removed) {
      delete q.from_time;
      delete q.to_time;
      delete q.startDate;
      delete q.endDate;
      delete q.startDateValue;
      delete q.endDateValue;
      history.replace({
        pathname: history.location.pathname,
        search: queryString.stringify(q),
      });
    }

    setRangeActive(false);
    setDateRange({
      startDate: null,
      endDate: null,
      startDateValue: null,
      endDateValue: null,
    });
    setStartDate('');
    setStartTime('');
    setEndDate('');
    setEndTime('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmModalClose = () => {
    setIsActionCancel(false);
    setIsActionRenew(false);
    setIsConfirmModalOpen(false);
  };

  const handleAmountChange = (event) => {
    const newAmount = event.target.value;
    setSelectedRow((prev) => ({
      ...prev,
      amount: newAmount,
    }));
  };

  // Build payload per new CMS* request contracts (no log_id)
  const buildTxnPayload = (row, extra = {}) => ({
    transaction_id: row?.transaction_id ?? undefined,
    provider_id: row?.provider_id ?? undefined,
    provider_code: row?.provider_code ?? undefined,
    platform_code: row?.platform_code ?? undefined,
    player_id: row?.player_id ?? undefined,
    provider_transaction_id: row?.provider_transaction_id ?? undefined,
    transaction_key: row?.transaction_key ?? undefined,
    ...extra,
  });

  const handleConfirmModalConfirm = async () => {
    const secondConfirm = window.confirm(
      'Are you really sure you want to proceed? This action cannot be undone.',
    );
    if (!secondConfirm) return;

    setIsLoading(true);
    const payload = buildTxnPayload(
      selectedRow,
      isActionCancel
        ? {}
        : isActionRenew
        ? {}
        : { adjust_amount: Number(selectedRow?.amount ?? 0) },
    );

    const fn = isActionCancel
      ? () => cancelTransaction(payload)
      : isActionRenew
      ? () => renewTransaction(payload)
      : () => resettleTransaction(payload);

    fn()
      .then((res) => {
        toast.success(_.get(res, 'data.message', 'Success'));
        mutate();
        handleConfirmModalClose();
      })
      .catch((error) => {
        toast.error(_.get(error, 'response.data.message', 'error'));
      })
      .finally(() => {
        setIsLoading(false);
        setIsActionCancel(false);
        setIsActionRenew(false);
      });
  };

  const handleProviderChange = (p) => {
    const normalized = {
      providerCode: p.providerCode ?? p.provider_code ?? '',
      providerId: p.providerId ?? p.provider_id ?? '',
      providerName: p.providerName ?? p.provider_name ?? '',
    };
    setCurrentProvider(normalized);
    setPage(0);
  };

  useEffect(() => {
    console.log('currentProvider ->', currentProvider);
  }, [currentProvider]);

  const onChangeDateRange = (range) => {
    setDateRange(range);
    setRangeActive(Boolean(range?.startDateValue && range?.endDateValue));

    const current = queryString.parse(history.location.search);
    const next = {
      ...current,
      ...(range.startDateValue ? { startDateValue: range.startDateValue } : {}),
      ...(range.endDateValue ? { endDateValue: range.endDateValue } : {}),
      ...(range.startDate ? { startDate: range.startDate } : {}),
      ...(range.endDate ? { endDate: range.endDate } : {}),
    };
    if (!range.startDateValue && !range.startDate) {
      delete next.startDateValue;
      delete next.startDate;
    }
    if (!range.endDateValue && !range.endDate) {
      delete next.endDateValue;
      delete next.endDate;
    }
    const s = queryString.stringify(next);
    history.replace({ pathname: '', search: s ? `?${s}` : '', state: { isActive: true } });
  };

  const { player_id: pidFromParams } = useParams();
  const playerId = pidFromParams || location.pathname.split('/')[2];

  const pickPlayer = (r) => {
    const d = r?.data ?? r;
    return d?.data?.player ?? d?.player ?? null;
  };

  const {
    data: profileData,
    error: profileErr,
    isValidating: profileLoading,
  } = useSWR(
    playerId ? ['getUserPlayerById', playerId] : null,
    async () => {
      const r = await getUserPlayerById(playerId);
      const player = pickPlayer(r);
      if (!player) throw new Error('No player in response');
      return player;
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  useEffect(() => {
    if (profileErr) toast.error('Failed to load player profile');
  }, [profileErr]);

  const [formError, setFormError] = useState('');
  const [transactionIdText, setTransactionIdText] = useState(''); // searches provider_transaction_id
  const [roundIdText, setRoundIdText] = useState('');
  const [userTokenText, setUserTokenText] = useState('');

  const diffInHours = (startISO, endISO) => {
    if (!startISO || !endISO) return 0;
    const start = new Date(startISO).getTime();
    const end = new Date(endISO).getTime();
    return (end - start) / (1000 * 60 * 60);
  };

  const toLocalInput = (dtLike) => {
    if (!dtLike) return '';
    const d = new Date(dtLike);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  };

  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [tzKey, setTzKey] = useState('GMT+7');
  const tzOffsetMin = parseTzKeyToOffset(tzKey);

  useEffect(() => {
    const s = dateRange?.startDateValue || dateRange?.startDate;
    const e = dateRange?.endDateValue || dateRange?.endDate;

    if (s) {
      const { date, time } = toDateAndTimeInTZ(s, tzOffsetMin);
      setStartDate(date);
      setStartTime(time);
    } else {
      setStartDate('');
      setStartTime('');
    }

    if (e) {
      const { date, time } = toDateAndTimeInTZ(e, tzOffsetMin);
      setEndDate(date);
      setEndTime(time);
    } else {
      setEndDate('');
      setEndTime('');
    }

    if (!s && !e) {
      setDateRange({ startDate: null, endDate: null, startDateValue: null, endDateValue: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const s = dateRange?.startDateValue || dateRange?.startDate;
    const e = dateRange?.endDateValue || dateRange?.endDate;
    if (s) {
      const { date, time } = toDateAndTimeInTZ(s, tzOffsetMin);
      setStartDate(date);
      setStartTime(time);
    }
    if (e) {
      const { date, time } = toDateAndTimeInTZ(e, tzOffsetMin);
      setEndDate(date);
      setEndTime(time);
    }
  }, [tzOffsetMin, dateRange]);

  const [quickRange, setQuickRange] = useState('');

  const handleQuickRangeChange = (e) => {
    const v = e.target.value;
    setQuickRange(v);
    applyPreset(v);
  };

  useEffect(() => {
    if (quickRange) applyPreset(quickRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tzOffsetMin]);

  const applyPreset = (type) => {
    const now = new Date();
    let start, end;

    if (type === '1h') {
      end = now;
      start = new Date(now.getTime() - 60 * 60 * 1000);
    } else if (type === '6h') {
      end = now;
      start = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    } else if (type === 'today') {
      start = new Date(now);
      start.setHours(0, 0, 0, 0);
      end = now;
    } else if (type === 'yesterday') {
      start = new Date(now);
      start.setDate(start.getDate() - 1);
      start.setHours(0, 0, 0, 0);
      end = new Date(now);
      end.setDate(end.getDate() - 1);
      end.setHours(23, 59, 59, 999);
    } else {
      return;
    }

    const [sDate, sTime] = toLocalInput(start).split('T');
    const [eDate, eTime] = toLocalInput(end).split('T');

    const startOffsetISO = fromDateTimeToOffsetISO(sDate, sTime, tzOffsetMin);
    const endOffsetISO = fromDateTimeToOffsetISO(eDate, eTime, tzOffsetMin);

    setStartDate(sDate);
    setStartTime(sTime);
    setEndDate(eDate);
    setEndTime(eTime);

    onChangeDateRange({
      startDate: `${sDate} ${sTime} (${tzKey})`,
      endDate: `${eDate} ${eTime} (${tzKey})`,
      startDateValue: startOffsetISO,
      endDateValue: endOffsetISO,
    });

    setPage(0);
  };

  const hasRange = Boolean(dateRange?.startDateValue && dateRange?.endDateValue);
  const useTimes = rangeActive && hasRange;

  const { data, error, mutate } = useSWR(
    [
      'getUserTransactionList',
      page,
      rowsPerPage,
      searchObj,
      useTimes ? dateRange.startDateValue : '',
      useTimes ? dateRange.endDateValue : '',
      currentProvider?.providerCode || '',
    ],
    () =>
      getUserTransactionList({
        playerId: playerId,
        page: page + 1,
        limit: rowsPerPage,
        fromTime: useTimes ? dateRange.startDateValue : undefined,
        toTime: useTimes ? dateRange.endDateValue : undefined,
        ticketId: searchObj?.ticketNumber,
        providerTransactionId: searchObj?.transactionId, // switched search to provider_transaction_id
        roundId: searchObj?.roundId,
        userToken: searchObj?.userToken,
        logTime: searchObj?.logTime,
        providerCode: currentProvider?.providerCode || undefined,
      }),
    { revalidateOnFocus: false },
  );

  const displayUsername = profileData?.username || '';
  const displayPlatformName =
    (profileData?.platform_name && profileData.platform_name.trim()) ||
    profileData?.platform_code ||
    '';

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    if (event.target.value === -1) {
      event.target.value = parseInt(0, 10);
      setRowsPerPage(0);
    } else {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    }
    setPage(0);
  };

  // Columns: show provider_transaction_id under label "transaction_id"
  const columns = [
    { key: 'log_time', label: 'log_time' },
    { key: 'player_id', label: 'player_id' },
    { key: 'username', label: 'username' },
    { key: 'provider_name', label: 'provider_name' },
    { key: 'platform_name', label: 'platform_name' },
    { key: 'game_name', label: 'game_name' },
    { key: 'provider_transaction_id', label: 'transaction_id' }, // <— swap in provider_transaction_id
    { key: 'round_id', label: 'round_id' },
    { key: 'transaction_type', label: 'transaction_type' },
    { key: 'amount', label: 'amount' },
  ];

  const renderCell = (row, key) => {
    if (key === 'log_time') return formatInTZ(row[key], tzOffsetMin);
    return row?.[key] ?? '';
  };

  const handleCancelButtonClick = (row) => {
    setSelectedRow(row);
    setIsActionCancel(true);
    setIsConfirmModalOpen(true);
  };

  const handleResettleButtonClick = (row) => {
    setSelectedRow(row);
    setIsActionCancel(false);
    setIsConfirmModalOpen(true);
  };

  const handleRenewButtonClick = (row) => {
    setSelectedRow(row);
    setIsActionRenew(true);
    setIsConfirmModalOpen(true);
  };

  const handleResyncButtonClick = (row) => {
    setIsLoading(true);
    const payload = buildTxnPayload(row);
    resyncTransaction(payload) // NOTE: ensure API helper uses POST now
      .then((res) => {
        toast.success(_.get(res, 'data.message', 'Success'));
        mutate();
      })
      .catch((error) => {
        toast.error(_.get(error, 'response.data.message', 'error'));
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <ApplicationLayout>
      <Breadcrumbs />

      <Grid container spacing={2} className={classes.compactRow} alignItems="center">
        <Grid item>
          <TextField
            label="Username"
            value={profileLoading ? 'Loading…' : displayUsername}
            variant="outlined"
            size="small"
            className={classes.compactField}
            disabled
          />
        </Grid>
        <Grid item>
          <TextField
            label="Player ID"
            value={playerId || ''}
            variant="outlined"
            size="small"
            className={classes.compactField}
            disabled
          />
        </Grid>
        <Grid item>
          <TextField
            label="Platform Name"
            value={profileLoading ? 'Loading…' : displayPlatformName}
            variant="outlined"
            size="small"
            className={classes.compactField}
            disabled
          />
        </Grid>
      </Grid>

      <Paper className={classes.filterBar} elevation={1}>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            const startOffsetISO = fromDateTimeToOffsetISO(startDate, startTime, tzOffsetMin);
            const endOffsetISO = fromDateTimeToOffsetISO(endDate, endTime, tzOffsetMin);

            const hasTxn = !!(transactionIdText || '').trim();
            const hasRound = !!(roundIdText || '').trim();
            const hasUser = !!(userTokenText || '').trim();
            const hasAnyText = hasTxn || hasRound || hasUser;
            const hasBothTimes = !!startOffsetISO && !!endOffsetISO;
            if (!hasAnyText && !hasBothTimes) {
              setFormError(
                'Enter at least one of Transaction ID / Round ID / User Token, or provide both Start and End time.',
              );
              return;
            }
            if (hasBothTimes) {
              const hours = diffInHours(startOffsetISO, endOffsetISO);
              if (hours < 0) {
                setFormError('End Time must be after Start Time.');
                return;
              }
              if (hours > 24) {
                setFormError('The selected time range cannot be longer than 24 hours.');
                return;
              }
            }
            setFormError('');
            setRangeActive(true);

            onChangeDateRange({
              startDate: startOffsetISO ? `${startDate} ${startTime} (${tzKey})` : null,
              endDate: endOffsetISO ? `${endDate} ${endTime} (${tzKey})` : null,
              startDateValue: startOffsetISO || null,
              endDateValue: endOffsetISO || null,
            });

            const nextSearch = { ...defaultSearchObj };
            nextSearch.transactionId = (transactionIdText || '').trim(); // provider_transaction_id
            nextSearch.roundId = (roundIdText || '').trim();
            nextSearch.userToken = (userTokenText || '').trim();
            setSearch(nextSearch);

            setPage(0);
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Provider */}
            <Grid item>
              <DropdownActiveProvider handleCallback={handleProviderChange} />
            </Grid>
            {/* Transaction ID (searches provider_transaction_id) */}
            <Grid item>
              <TextField
                label="Transaction ID"
                variant="outlined"
                size="small"
                className={classes.searchField}
                value={transactionIdText}
                onChange={(e) => setTransactionIdText(e.target.value || '')}
                InputProps={{ onFocus: (e) => e.target.select() }}
              />
            </Grid>
            {/* Round ID */}
            <Grid item>
              <TextField
                label="Round ID"
                variant="outlined"
                size="small"
                className={classes.searchField}
                value={roundIdText}
                onChange={(e) => setRoundIdText(e.target.value || '')}
                InputProps={{ onFocus: (e) => e.target.select() }}
              />
            </Grid>
            {/* User Token */}
            <Grid item>
              <TextField
                label="User Token"
                variant="outlined"
                size="small"
                className={classes.searchField}
                value={userTokenText}
                onChange={(e) => setUserTokenText(e.target.value || '')}
                InputProps={{ onFocus: (e) => e.target.select() }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="center" style={{ marginTop: 8 }}>
            {/* Start: Date + Time */}
            <Grid item>
              <TextField
                label="Start Date"
                type="date"
                variant="outlined"
                size="small"
                className={classes.dateField}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Start Time"
                type="time"
                variant="outlined"
                size="small"
                className={classes.timeField}
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>
            {/* End: Date + Time */}
            <Grid item>
              <TextField
                label="End Date"
                type="date"
                variant="outlined"
                size="small"
                className={classes.dateField}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item>
              <TextField
                label="End Time"
                type="time"
                variant="outlined"
                size="small"
                className={classes.timeField}
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
              />
            </Grid>
            {/* Timezone */}
            <Grid item>
              <TextField
                select
                label="Timezone"
                variant="outlined"
                size="small"
                className={classes.tzField}
                value={tzKey}
                onChange={(e) => setTzKey(e.target.value)}
              >
                {AllTimeZone.map((tz) => (
                  <MenuItem key={tz.key} value={tz.key}>
                    {tz.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {/* Quick range preset dropdown */}
            <Grid item xs={12}>
              <TextField
                select
                label="Quick time range"
                variant="outlined"
                size="small"
                className={classes.tzField}
                value={quickRange}
                onChange={handleQuickRangeChange}
              >
                <MenuItem value="" disabled>
                  Select a preset…
                </MenuItem>
                <MenuItem value="1h">Last 1 hour</MenuItem>
                <MenuItem value="6h">Last 6 hours</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
              </TextField>
            </Grid>
            {formError ? (
              <Box mt={1}>
                <Typography variant="caption" color="error">
                  {formError}
                </Typography>
              </Box>
            ) : null}

            <Grid container spacing={2} className={classes.buttonRow}>
              <Grid item>
                <Button type="submit" variant="contained" color="primary">
                  Apply
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setRangeActive(false);
                    setFormError('');
                    setTransactionIdText('');
                    setRoundIdText('');
                    setUserTokenText('');
                    setStartDate('');
                    setStartTime('');
                    setEndDate('');
                    setEndTime('');
                    setSearch(defaultSearchObj);
                    setQuickRange('');
                    onChangeDateRange({
                      startDate: null,
                      endDate: null,
                      startDateValue: null,
                      endDateValue: null,
                    });
                    setPage(0);
                  }}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Typography variant="h6" gutterBottom component="div">
        User Player List
      </Typography>

      <Box>
        <Grid item xs={12}>
          <TableContainer className={classes.tableContainer}>
            <Table className={classes.table} aria-label="table">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c.key}>{c.label}</TableCell>
                  ))}
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.transactions ?? []).map((row, index) => (
                  <Fragment key={index}>
                    <TableRow key={`${index}_player`}>
                      {columns.map((c) => (
                        <TableCell key={c.key}>{renderCell(row, c.key)}</TableCell>
                      ))}
                      <TableCell>
                        <div
                          style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          {user.role == 1000 && (
                            <>
                              <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                onClick={() => handleResyncButtonClick(row)}
                              >
                                Resync
                              </Button>
                              {row.transaction_type === 'bet' && (
                                <Button
                                  type="button"
                                  variant="contained"
                                  color="primary"
                                  disabled={isLoading}
                                  onClick={() => handleCancelButtonClick(row)}
                                >
                                  Cancel
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                disabled={isLoading}
                                onClick={() => handleResettleButtonClick(row)}
                              >
                                Adjust
                              </Button>
                              {row.transaction_type !== 'adjustment' && (
                                <Button
                                  type="button"
                                  variant="contained"
                                  color="primary"
                                  disabled={isLoading}
                                  onClick={() => handleRenewButtonClick(row)}
                                >
                                  Renew
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[25, 50, 100, 500]}
                    count={data?.total || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{ inputProps: { 'aria-label': 'rows per page' }, native: true }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Box>

      <ConfirmModal
        title={isActionCancel ? 'Cancel' : isActionRenew ? 'Renew' : 'Adjust'}
        description={
          <Box>
            <TextField
              size="small"
              variant="outlined"
              label="Amount"
              value={selectedRow?.amount || 0}
              disabled={isActionCancel || isActionRenew}
              onChange={handleAmountChange}
            />
            <Divider style={{ borderBottomWidth: 2, margin: '16px 0' }} />
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>Provider Transaction Id:</TableCell>
                  <TableCell>{selectedRow?.provider_transaction_id}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Game Name:</TableCell>
                  <TableCell>{selectedRow?.game_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Provider:</TableCell>
                  <TableCell>{selectedRow?.provider_name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Username:</TableCell>
                  <TableCell>{selectedRow?.username}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Player Id:</TableCell>
                  <TableCell>{selectedRow?.player_id}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        }
        open={isConfirmModalOpen}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        confirmText="Apply"
        cancelText="Cancel"
      />
    </ApplicationLayout>
  );
};

export default UserTransaction;
